import { useEffect } from 'react';
import { Alert } from "antd";
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  MoneyDisplay,
  Spinner,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import { ROUTES } from '@shared/config/routes';
import { useBuyRequestQuery, canSubmitAgainst, type RfqStatus } from '@entities/rfq';
import { useCategoriesQuery, useCategoryName } from '@entities/taxonomy';
import { UserCell } from '@entities/user-directory';

const STATUS_TONE: Record<RfqStatus, 'success' | 'neutral' | 'warning'> = {
  OPEN: 'success',
  CLOSED: 'neutral',
  EXPIRED: 'warning',
};

const STATUS_LABEL: Record<RfqStatus, string> = {
  OPEN: 'Open',
  CLOSED: 'Closed',
  EXPIRED: 'Expired',
};

interface BuyRequestDetailProps {
  id: string;
  // Spec §7 v1.2 — embedded compact mode is used by the respond form
  // so it shows the same context without the page chrome.
  variant?: 'page' | 'embed';
}

export function BuyRequestDetail({ id, variant = 'page' }: BuyRequestDetailProps) {
  const navigate = useNavigate();
  const { data: rfq, isLoading, error, isError } = useBuyRequestQuery(id);
  const { data: categories } = useCategoriesQuery();
  const localize = useCategoryName();

  // §7 v1.2 UX: 403/404 → redirect to feed with an explanation toast.
  // Built as inline banner + auto-redirect; we don't ship a toast hub yet.
  useEffect(() => {
    if (!isError) return;
    const apiError = error instanceof ApiError ? error : null;
    if (apiError?.status !== 403 && apiError?.status !== 404) return;
    const t = setTimeout(() => navigate(ROUTES.buyRequests, { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [isError, error, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    const apiError = error instanceof ApiError ? error : null;
    const message =
      apiError?.status === 403
        ? 'This request is no longer available to you.'
        : apiError?.status === 404
          ? 'Buy request not found.'
          : (apiError?.message ?? 'Failed to load request.');
    return (
      <Alert
        type="error"
        showIcon
        message={message}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.buyRequests, { replace: true })}
          >
            Back to feed
          </Button>
        }
      />
    );
  }

  if (!rfq) return null;

  const matchedCategory = categories?.find((c) => c.id === rfq.category);
  const categoryName = matchedCategory ? localize(matchedCategory) : rfq.category;
  const canRespond = canSubmitAgainst(rfq);

  return (
    <div className="flex flex-col gap-4">
      {rfq.hiddenByAdmin ? (
        <Alert
          type="error"
          showIcon
          message="This request was hidden by the administrator."
          description={rfq.hiddenReason ?? undefined}
        />
      ) : null}

      {rfq.status !== 'OPEN' ? (
        <Alert type="warning" showIcon message={<>This request is <strong>{STATUS_LABEL[rfq.status].toLowerCase()}</strong>. You can view it but
          cannot submit a new quote.</>} />
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div className="flex flex-col gap-1">
            <CardTitle>{rfq.name}</CardTitle>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Badge tone={STATUS_TONE[rfq.status]}>{STATUS_LABEL[rfq.status]}</Badge>
              {rfq.dispatch === 'TARGETED' ? <Badge tone="primary">Targeted</Badge> : null}
              {rfq.alreadyQuoted ? <Badge tone="info">You already responded</Badge> : null}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {categoryName}
              {rfq.hsCode ? ` · HS ${rfq.hsCode}` : ''}
            </p>
            <div className="mt-2">
              <UserCell userId={rfq.buyerId} size="sm" showRole />
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="font-medium">
              <MoneyDisplay money={rfq.budget} fallback="Budget: —" />
            </div>
            <div className="text-xs text-muted-foreground">
              {rfq.quoteCount} quote{rfq.quoteCount === 1 ? '' : 's'}
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Volume" value={rfq.volumeText ?? '—'} />
          <Field label="Destination" value={rfq.destinationCountry ?? '—'} />
          <Field
            label="Deadline"
            value={rfq.deadline ? new Date(rfq.deadline).toLocaleDateString() : '—'}
          />
          <Field label="Created" value={new Date(rfq.createdAt).toLocaleString()} />
        </CardContent>
        {rfq.techDescription ? (
          <CardContent className="border-t pt-4">
            <h4 className="mb-2 text-sm font-medium">Description</h4>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{rfq.techDescription}</p>
          </CardContent>
        ) : null}
        {rfq.attachments.length > 0 ? (
          <CardContent className="border-t pt-4">
            <h4 className="mb-2 text-sm font-medium">Attachments</h4>
            <ul className="flex flex-col gap-1 text-sm">
              {rfq.attachments.map((link) =>
                link.url ? (
                  <li key={link.id}>
                    <a href={link.url} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
                      {link.id.slice(0, 12)}…
                    </a>
                  </li>
                ) : (
                  <li key={link.id} className="font-mono text-xs opacity-70">
                    {link.id}
                  </li>
                ),
              )}
            </ul>
          </CardContent>
        ) : null}
      </Card>

      {variant === 'page' ? (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(ROUTES.buyRequests)}>
            Back to feed
          </Button>
          {rfq.alreadyQuoted ? (
            <Button onClick={() => navigate(ROUTES.quotes)}>View my quotes</Button>
          ) : (
            <Button
              disabled={!canRespond}
              title={!canRespond ? 'Request is not open for new responses' : undefined}
              onClick={() => navigate(`${ROUTES.buyRequests}/${rfq.id}/respond`)}
            >
              Respond
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );
}
